import React, { useState } from 'react'
import TextFilter from './fields/TextFilter'
import SelectFilter from './fields/SelectFilter'
import DateRangeFilter from './fields/DateRangeFilter'
import TimeRangeFilter from './fields/TimeRangeFilter'
import DateTimeRangeFilter from './fields/DateTimeRangeFilter'
import { serializeFilters } from './serialize'
import { t } from '../../../i18n'
import {
  FilterField,
  FilterValue,
  FilterValues,
  RangeValue,
  emptyRange,
  emptyValues,
  isRangeKind,
  isRangeValue
} from './types'

const scope = 'table_filters'

interface TableFiltersProps {
  fields: FilterField[]
  initialValues?: FilterValues
  leadingControls?: React.ReactNode
  primaryControls?: React.ReactNode
  onApply: (params: Record<string, string>) => void
}

const asString = (value: FilterValue): string =>
  typeof value === 'string' ? value : ''
const asRange = (value: FilterValue): RangeValue =>
  isRangeValue(value) ? value : emptyRange()

const columnClass = (field: FilterField): string =>
  isRangeKind(field.kind) ? 'col-md-4' : 'col-md-2'

const TableFilters: React.FC<TableFiltersProps> = ({
  fields,
  initialValues,
  leadingControls,
  primaryControls,
  onApply
}) => {
  const [values, setValues] = useState<FilterValues>(() => ({
    ...emptyValues(fields),
    ...initialValues
  }))
  const [expanded, setExpanded] = useState(false)

  const updateValue = (id: string, value: FilterValue) => {
    setValues((previous) => ({ ...previous, [id]: value }))
  }

  const applyNow = () => {
    onApply(serializeFilters(fields, values))
  }

  const clearAll = () => {
    setValues(emptyValues(fields))
    onApply({})
  }

  const renderField = (field: FilterField) => {
    const inputId = `filter-${field.id}`

    switch (field.kind) {
      case 'text':
        return (
          <TextFilter
            inputId={inputId}
            label={field.label}
            value={asString(values[field.id])}
            placeholder={field.placeholder}
            onChange={(value) => updateValue(field.id, value)}
          />
        )
      case 'select':
        return (
          <SelectFilter
            inputId={inputId}
            label={field.label}
            value={asString(values[field.id])}
            options={field.options ?? []}
            onChange={(value) => updateValue(field.id, value)}
          />
        )
      case 'dateRange':
        return (
          <DateRangeFilter
            label={field.label}
            value={asRange(values[field.id])}
            onChange={(value) => updateValue(field.id, value)}
          />
        )
      case 'timeRange':
        return (
          <TimeRangeFilter
            label={field.label}
            value={asRange(values[field.id])}
            onChange={(value) => updateValue(field.id, value)}
          />
        )
      case 'dateTimeRange':
        return (
          <DateTimeRangeFilter
            label={field.label}
            value={asRange(values[field.id])}
            onChange={(value) => updateValue(field.id, value)}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="card mb-3 table-filters">
      <div
        className={`table-filters__bar${expanded ? '' : ' border-bottom-0'}`}
      >
        {leadingControls && (
          <div className="table-filters__leading">{leadingControls}</div>
        )}
        {primaryControls && (
          <div className="table-filters__primary">{primaryControls}</div>
        )}
        {(leadingControls || primaryControls) && (
          <span className="table-filters__divider" aria-hidden="true" />
        )}
        <button
          type="button"
          className={`table-filters__toggle${expanded ? ' table-filters__toggle--open' : ''}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((previous) => !previous)}
        >
          {t('filters', { scope })}
          <i
            className={`fa fa-chevron-${expanded ? 'up' : 'down'} table-filters__chevron`}
          />
        </button>
      </div>

      {expanded && (
        <div className="card-body">
          <div className="row g-3">
            {fields.map((field) => (
              <div className={columnClass(field)} key={field.id}>
                {renderField(field)}
              </div>
            ))}
          </div>

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={applyNow}
            >
              {t('search', { scope })}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={clearAll}
            >
              {t('clear', { scope })}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableFilters
